import type { CaseFormValues } from "@/components/Forms/CaseForm.tsx";
import type { CaseFilterEditing } from "@/queries/useFetchCaseFiltersets.ts";
import { useFetchEmployeeByType } from "@/queries/useFetchEmployeeByType.ts";
import {
    Autocomplete,
    Checkbox,
    Chip,
    CircularProgress,
    Paper,
    type SxProps,
    TextField,
    Typography,
} from "@mui/material";
import { Check } from "lucide-react";
import type React from "react";
import { useFormContext } from "react-hook-form";

type Props = {
    name: keyof CaseFormValues | keyof CaseFilterEditing;
    label: string;
    type: string;
    includeInactive?: boolean;
    sx?: SxProps;
};

const SELECT_ALL_VALUE = "__SELECT_ALL__";

const EmployeeNameByTypeMultiple: React.FC<Props> = ({
    name,
    label,
    type,
    sx,
    includeInactive = false,
}: Props) => {
    const form = useFormContext();
    const { data, isError, isRefetching, isLoading, error } = useFetchEmployeeByType({
        type: type,
        includeInactive: includeInactive,
    });

    const fieldValue = form.watch(name) ?? [];

    const handleUserSelection = (userIds: string[]) => {
        form.setValue(name, userIds);
    };

    const getAvailableUsers = () => {
        return (
            data?.map((employee) => ({
                id: employee.idUUID,
                label: `${employee.nameFirst} ${employee.nameLast} (${employee.dashboardVersion})`,
            })) ?? []
        );
    };

    const getSelectedUsers = () => {
        return getAvailableUsers().filter((employee) => fieldValue.includes(employee.id));
    };

    const users = getAvailableUsers();
    const allSelected = fieldValue.length > 0 && fieldValue.length === users.length;

    const selectAllOption = {
        id: SELECT_ALL_VALUE,
        label: allSelected ? "✓ Deselect All" : "Select All",
    };

    const usersWithSelectAll = [selectAllOption, ...users];

    if (isError) {
        return (
            <Paper
                sx={{
                    p: 2,
                    borderRadius: "8px",
                }}
            >
                <Typography variant={"h5"}>Error loading employees</Typography>
                <Typography variant={"body1"}>{error.message}</Typography>
            </Paper>
        );
    }

    if (isLoading || isRefetching) {
        return (
            <Paper
                sx={{
                    p: 2,
                    borderRadius: "8px",
                }}
            >
                <CircularProgress size={"12px"} />
            </Paper>
        );
    }

    return (
        <Autocomplete
            multiple
            options={usersWithSelectAll}
            value={getSelectedUsers()}
            onChange={(_event, newValue) => {
                const hasSelectAll = newValue.some((item) => item.id === SELECT_ALL_VALUE);

                if (hasSelectAll) {
                    if (allSelected) {
                        handleUserSelection([]);
                    } else {
                        handleUserSelection(users.map((user) => user.id));
                    }
                } else {
                    const normalValues = newValue
                        .filter((item) => item.id !== SELECT_ALL_VALUE)
                        .map((item) => item.id);
                    handleUserSelection(normalValues);
                }
            }}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option, { selected }) => {
                const isSelectAll = option.id === SELECT_ALL_VALUE;
                const { key, ...otherProps } = props;

                return (
                    <li {...otherProps} key={option.id}>
                        <Checkbox
                            icon={
                                <span
                                    style={{
                                        width: 18,
                                        height: 18,
                                        border: "2px solid #ccc",
                                        borderRadius: 3,
                                    }}
                                />
                            }
                            checkedIcon={<Check size={18} />}
                            style={{ marginRight: 8 }}
                            checked={isSelectAll ? allSelected : selected}
                        />
                        <span style={{ fontWeight: isSelectAll ? 600 : 400 }}>{option.label}</span>
                    </li>
                );
            }}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                    <Chip
                        variant="outlined"
                        label={option.label}
                        {...getTagProps({ index })}
                        key={option.id}
                    />
                ))
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={label}
                    error={!!form.formState.errors.notifyUsers}
                    helperText={form.formState.errors.notifyUsers?.message as string}
                />
            )}
            sx={{
                mb: 1,
                ...sx,
            }}
        />
    );
};

export default EmployeeNameByTypeMultiple;
